import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { Dialog } from "primereact/dialog";
import { Button as ButtonPrime } from "primereact/button";
import api from "../api";
import { Timezone, getWeek, handleApiError } from "../lib/helper";
import "moment-timezone";
import { saveAs } from "file-saver";
import * as Sentry from "@sentry/browser";
import useTranslation from "../i18n/useTranslation";
import { TASK_TYPE_LABEL_KEYS } from "../consts/taskTypes";

const ReportModal = ({ users, taskTypes, firstScheduleDate, selectedProductCode }) => {
   const { t } = useTranslation();
   const [reportModalVisibility, setReportModalVisibility] = useState(false);
   const [dateRange, setDateRange] = useState([new Date(), new Date()]);
   const [selectedResidents, setSelectedResidents] = useState([]);
   const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);
   const [isRequesting, setIsRequesting] = useState(false);

   const [startDate, endDate] = dateRange;

   useEffect(() => {
      if (firstScheduleDate) {
         const week = getWeek(firstScheduleDate);
         setDateRange([new Date(week[0]), new Date(week[6])]);
      }
   }, [firstScheduleDate]);

   const handleDateChange = (e) => setDateRange(e.value);

   const handleResidentsChange = (e) => setSelectedResidents(e.value || []);

   const handleTaskTypesChange = (e) => setSelectedTaskTypes(e.value || []);

   const handleOpenReportModal = () => setReportModalVisibility(!reportModalVisibility);

   const handleSubmit = async () => {
      try {
         setIsRequesting(true);

         const adjustDateIfLate = (date) => {
            const momentDate = moment(date).tz(Timezone);
            const hour = momentDate.hour();
            const format = "YYYY-MM-DD[T]HH:mm:ss";
            
            if (hour === 23) {
               return momentDate.add(1, "hour").format(format) + "Z";
            } else if (hour === 22) {
               return momentDate.add(2, "hour").format(format) + "Z";
            }

            return momentDate.format(format) + "Z";
         };

         const data = {
            dataOd: adjustDateIfLate(startDate),
            dataDo: adjustDateIfLate(endDate),
            uzytkownicyId: selectedResidents,
            typyZadan: selectedTaskTypes,
            kodProduktu: selectedProductCode
         };

         const response = await api.generateReport(data);
         const contentDisposition = response.headers["content-disposition"];

         const fileName = contentDisposition
            ? contentDisposition.split("filename=")[1].replace(/"/g, "")
            : `zadania-raport_${moment().tz(Timezone).format("YYYY-MM-DD")}.xlsx`;

         saveAs(response.data, fileName);
         Sentry.withScope((scope) => {
            scope.setLevel("info");
            scope.setExtra("data", data);
            Sentry.captureMessage("generate-report");
         });
         handleOpenReportModal();
      } catch (err) {
         handleApiError(err, "generateReportError");
      } finally {
         setIsRequesting(false);
      }
   };

   const taskTypeOptions = taskTypes.map((type) => ({
      value: type,
      label: TASK_TYPE_LABEL_KEYS[type]
         ? t(TASK_TYPE_LABEL_KEYS[type])
         : type.replace(/([A-Z])/g, " $1").trim()
   }));

   const renderDatePicker = () => (
      <div className="field mb-3">
         <label className="block mb-2">{t("report.dateRange")}</label>
         <Calendar
            value={dateRange}
            onChange={handleDateChange}
            selectionMode="range"
            showIcon
            className="w-full"
            maxDate={moment().add(1, "year").toDate()}
            minDate={moment().subtract(1, "year").toDate()}
            placeholder={t("report.placeholders.dateRange")}
            hideOnDateTimeSelect
         />
      </div>
   );

   const renderResidentsSelect = () => (
      <div className="field mb-3">
         <label className="block mb-2">{t("report.users")}</label>
         <MultiSelect
            value={selectedResidents}
            onChange={handleResidentsChange}
            options={users}
            optionLabel="imieNazwisko"
            optionValue="UzytkownikGlobID"
            placeholder={t("report.placeholders.users")}
            className="w-full"
            filter
            showClear
         />
      </div>
   );

   const renderTaskTypesSelect = () => (
      <div className="field mb-3">
         <label className="block mb-2">{t("report.taskType")}</label>
         <MultiSelect
            value={selectedTaskTypes}
            onChange={handleTaskTypesChange}
            options={taskTypeOptions}
            optionLabel="label"
            optionValue="value"
            placeholder={t("report.placeholders.taskTypes")}
            className="w-full"
            filter
            showClear
         />
      </div>
   );

   const renderModalContent = () => (
      <>
         <p className="text-xl mb-4">{t("report.selectParams")}</p>
         {renderDatePicker()}
         {renderResidentsSelect()}
         {renderTaskTypesSelect()}
         <div className="flex justify-content-end gap-2">
            <ButtonPrime onClick={handleOpenReportModal} label={t("common.actions.cancel")} severity="secondary" />
            <ButtonPrime
               onClick={handleSubmit}
               label={isRequesting ? t("report.generating") : t("report.generate")}
               icon="pi pi-download"
               loading={isRequesting}
               disabled={isRequesting}
            />
         </div>
      </>
   );

   return (
      <>
         <ButtonPrime
            onClick={handleOpenReportModal}
            disabled={isRequesting}
            className="text--xl text-white font-bold"
            outlined
            rounded
         >
            {t("report.openButton")}
         </ButtonPrime>
         <Dialog
            visible={reportModalVisibility}
            onHide={handleOpenReportModal}
            header={t("report.dialogTitle")}
            className="w-full"
            modal
            style={{ maxWidth: "500px" }}
         >
            {renderModalContent()}
         </Dialog>
      </>
   );
};

const mapStateToProps = (state) => ({
   users: state.schedule.data.uzytkownicy || [],
   taskTypes: state.session.typyZadan || [],
   firstScheduleDate: state.schedule.data.dzienWiodacy || null,
   selectedProductCode: state.schedule.data.kodProduktu || ""
});

export default connect(mapStateToProps)(ReportModal);
